import { useEffect, useRef, useState } from "react";
import {
  AddUserModal,
  DeleteUserModal,
  LogoutModal,
  UpdateUserModal,
} from "../modal";
import { useRecoilState } from "recoil";
import { ModalTypeState, SelectedUserState } from "../../atoms";
import { UilRedo, UilSearch } from "@iconscout/react-unicons";
import { GetNewInstance, Store } from "../../helpers";
import { UserInfoListDto, UsersClient } from "../../services";
import { UserListItem } from "./UserListItem";
import { ArchiveUserModal } from "../modal/ArchiveUserModal";
import { ActivateUserModal } from "../modal/ActivateUserModal";
import { Pagination } from "../layouts";

export default function UserList() {
  const client = new UsersClient(undefined, GetNewInstance());
  const [users, setUsers] = useState<UserInfoListDto[]>([]);
  const [, setModalType] = useRecoilState(ModalTypeState);
  const [, setSelectedUser] = useRecoilState(SelectedUserState);
  const [totalPageNumber, setTotalPageNumber] = useState(1);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);
  const [canManage, setCanManage] = useState<boolean | null>(null);
  const [canEditContent, setCanEditContent] = useState<boolean | null>(null);
  const [canViewContent, setCanViewContent] = useState<boolean | null>(null);
  const [canUploadContent, setCanUploadContent] = useState<boolean | null>(
    null
  );

  const usersCustomerId = Store.getCustomerId();
  let timeOutId = useRef<NodeJS.Timeout>();

  useEffect(() => {
    retrieveUsers();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchTerm,
    canManage,
    canEditContent,
    canViewContent,
    canUploadContent,
    currentPageNumber,
  ]);

  useEffect(() => {
    setCurrentPageNumber(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrieveUsers = () => {
    clearTimeout(timeOutId.current);
    timeOutId.current = setTimeout(() => {
      client
        .getUsers(
          usersCustomerId ?? "",
          searchTerm,
          canManage,
          canEditContent,
          canViewContent,
          canUploadContent,
          currentPageNumber
        )
        .then((response) => {
          response?.data && setUsers(response?.data);
          response?.pageNumber && setCurrentPageNumber(response?.pageNumber);
          response?.pageCount && setTotalPageNumber(response?.pageCount);
        })
        .catch((error) => {
          console.log(error);
        });
    }, 300);
  };

  const onModalSuccess = () => {
    retrieveUsers();
    setSelectedUser(null);
    setModalType(null);
  };

  const onModalFailure = () => {
    setSelectedUser(null);
    setModalType(null);
  };

  return (
    <>
      <div className="h-full overflow-auto p-5 ">
        <div className="flex justify-between mb-5">
          <div>
            <div className="h-11 rounded-md border border-page-border flex items-center bg-white bg-opacity-5 overflow-hidden">
              <UilSearch className="w-5 h-5 mx-4" />
              <input
                className="h-full bg-transparent border-0"
                type="text"
                placeholder="Search for user"
                onChange={(e) => setSearchTerm(e.target.value)}
                value={searchTerm ?? ""}
              />
            </div>
          </div>
          <div className="flex flex-row items-center gap-3">
            {((searchTerm !== null && searchTerm !== "") ||
              canManage ||
              canEditContent ||
              canViewContent ||
              canUploadContent) && (
              <UilRedo
                className="text-gray-100 cursor-pointer"
                onClick={() => {
                  setSearchTerm("");
                  setCanManage(null);
                  setCanEditContent(null);
                  setCanViewContent(null);
                  setCanUploadContent(null);
                }}
              />
            )}
            <div className="flex items-center gap-2">
              <label className="flex items-center gap-2 px-1 py-1">
                <input
                  className="border border-gray-400 focus:shadow-none bg-input-border w-4 h-4"
                  type="checkbox"
                  checked={canManage ?? false}
                  onChange={(e) => setCanManage(e.target.checked)}
                />
                <span>Can manage</span>
              </label>
              <label className="flex items-center gap-2 px-1 py-1">
                <input
                  className="border border-gray-400 focus:shadow-none bg-input-border w-4 h-4"
                  type="checkbox"
                  checked={canEditContent ?? false}
                  onChange={(e) => setCanEditContent(e.target.checked)}
                />
                <span>Can edit</span>
              </label>
              <label className="flex items-center gap-2 px-1 py-1">
                <input
                  className="border border-gray-400 focus:shadow-none bg-input-border w-4 h-4"
                  type="checkbox"
                  checked={canViewContent ?? false}
                  onChange={(e) => setCanViewContent(e.target.checked)}
                />
                <span>Can view</span>
              </label>
              <label className="flex items-center gap-2 px-1 py-1">
                <input
                  className="border border-gray-400 focus:shadow-none bg-input-border w-4 h-4"
                  type="checkbox"
                  checked={canUploadContent ?? false}
                  onChange={(e) => setCanUploadContent(e.target.checked)}
                />
                <span>Can upload</span>
              </label>
            </div>
          </div>
        </div>
        <div className="border-b rounded-lg border-page-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-page-border">
                <td className="px-4 py-3">First name</td>
                <td className="px-4 py-3">Last name</td>
                <td className="px-4 py-3">Email</td>
                <td className="px-4 py-3">Permissions</td>
                <td className="px-4 py-3">Date added</td>
                <td className="px-4 py-3">Last login</td>
                <td className="px-4 py-3">Status</td>
                <td className="px-4 py-3"></td>
              </tr>
            </thead>
            <tbody>
              {users.map((x) => (
                <UserListItem key={x.email} User={x} />
              ))}
            </tbody>
          </table>
          <Pagination
            totalPageCount={totalPageNumber}
            currentPageNumber={currentPageNumber}
            setCurrentPageNumber={setCurrentPageNumber}
          />
        </div>
      </div>
      <AddUserModal
        onSuccess={onModalSuccess}
        onFailure={onModalFailure}
        existingUsers={users}
      />
      <ArchiveUserModal onSuccess={onModalSuccess} onFailure={onModalFailure} />
      <ActivateUserModal
        onSuccess={onModalSuccess}
        onFailure={onModalFailure}
      />
      <DeleteUserModal onSuccess={onModalSuccess} onFailure={onModalFailure} />
      <UpdateUserModal onSuccess={onModalSuccess} onFailure={onModalFailure} />
      <LogoutModal />
    </>
  );
}
